import React, { useState } from "react";
import { useQueryParams } from "utils/useQueryParams";
import SearchBar from "./SearchBar";
import TableBody from "./TableBody";
import TableHeader from "./TableHeader";
import TablePagination from "./TablePagination";
import Button from "components/Button";
import TableSkeleton from "./TableSkeleton";
import { useTranslation } from "react-i18next";
import { useMutationCustom } from "utils/useQueryCUstom";
import axios from "axios";
import apiEndpoints from "configs/apiEndPoints";
import toastFunc from "utils/toast";
import Alert from "components/Alert";
import Modal from "components/Modal";
import Duplication, { FormValues } from "./Duplication";

export interface TableColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  isWide?: boolean;
}

interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  onEdit?: (item: T) => void | null;
  onDelete?: (item: T) => void | null;
  onView?: (item: T) => void | null;
  rowsPerPage: number;
  total: number;
  isLoading: boolean;
  itemForBg?: string;
  deleteRoute: string;
  invalidQuery?: any[];
  noDeleteSelected?: boolean;
  hasDuplication?: boolean;
  duplicationApiAddress?: string;
}

const Table = <T extends Record<string, any>>({
  data = [],
  columns,
  duplicationApiAddress,
  onEdit,
  onDelete,
  onView,
  rowsPerPage = 10,
  total,
  isLoading,
  itemForBg,
  deleteRoute,
  invalidQuery,
  hasDuplication = false,
  noDeleteSelected = false,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const { getParam, setParams } = useQueryParams();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [formattedIds, setFormattedIds] = useState<string[]>([]);

  const currentPage = parseInt(getParam("page") || "1", 10);
  const searchText = getParam("searchText") || "";

  const handleSearchChange = (term: string) => {
    // setSearchParams({ page: "1", searchText: term });
    // navigate(`?page=1&searchText=${term}`);
    setParams({ searchText: term });
  };

  // -------------------------- delete action mutation
  const { mutate: deleteItem } = useMutationCustom({
    name: `delete_selected`,
    url: async (id: string) => await axios.delete(`${apiEndpoints[deleteRoute].base}/ids?${id}`),
    onSuccess: () => {
      toastFunc.success({ title: t("deleteSuccess") });
      setSelectedRows(new Set());
      // refetch();
    },
    invalidQuery: invalidQuery,
  });

  const handleDeleteAll = (data: any[]) => {
    const formattedIds = data.map((id) => `ids=${id}`).join("&");
    Alert.confirm({
      title: t("areYouSure"),
      message: `${t("wantToDelete", { changeablePart: t("selectedItems") })}`,
      onConfirm: () => deleteItem(formattedIds),
    });
  };

  const handlePageChange = (page: number) => {
    // setSearchParams({ page: page.toString(), searchText: searchTerm });
    // navigate(`?page=${page}&searchText=${searchTerm}`);
    setParams({ page });
  };

  const handleSortChange = (key: string, order: "asc" | "desc") => {
    setParams({ sortKey: key, sortOrder: order });
  };

  const handleToggleSelectRow = (index: number) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(index)) {
      newSelectedRows.delete(index);
    } else {
      newSelectedRows.add(index);
    }
    setSelectedRows(newSelectedRows);
  };

  const getSelectedIds = () => {
    return Array.from(selectedRows).map((index) => data[index]._id);
  };

  // ------------------------------------------------------------------------------------ Duplication
  const { mutate: mutateDuplicate, isLoading: duplicateLoading } = useMutationCustom({
    name: `duplicate_selected`,
    url: async (requestedData: any) => await axios.post(`${apiEndpoints[duplicationApiAddress || ""].base}/duplicate`, { ...requestedData, targetIds: formattedIds }),
    onSuccess: () => {
      toastFunc.success({ title: t("duplicate") });
      setSelectedRows(new Set());
      // refetch();
    },
    invalidQuery: invalidQuery,
  });

  const handleDuplicate = (data: string[]) => {
    console.log("🚀 ~ handleDuplicate ~ data:", data);
    setFormattedIds(data);
    setIsDuplicateModalOpen(true);
  };
  // on submit for duplication items
  const onSubmit = (data: FormValues) => {
    console.log("🚀 ~ onSubmit ~ data:", data);
    const requestData = {
      visaTypeIds: data.visaTypes.map((item) => item.value),
      countryPairIds: data.countryPairs.map((item) => item.value),
    };

    mutateDuplicate(requestData);
  };

  return (
    <div className="">
      <div className="flex justify-end mb-4 gap-4 items-center">
        {selectedRows.size > 0 && hasDuplication && (
          <Button variant="info" type="button" onClick={() => handleDuplicate(getSelectedIds())}>
            {t("duplicate")}
          </Button>
        )}
        {selectedRows.size > 0 && (
          <Button variant="danger" type="button" onClick={() => handleDeleteAll(getSelectedIds())}>
            {t("deleteSelected")}
          </Button>
        )}
        <SearchBar searchTerm={searchText} onSearchChange={handleSearchChange} />
      </div>
      {isLoading ? (
        <div className=" overflow-x-auto">
          <TableSkeleton />
        </div>
      ) : (
        <>
          <div className=" overflow-x-auto bg-sidebar">
            <table className="min-w-full bg-sidebar">
              <TableHeader
                columns={columns}
                selectAll={selectedRows.size === data.length}
                noDeleteSelected={noDeleteSelected}
                onToggleSelectAll={() => setSelectedRows(selectedRows.size === data.length ? new Set() : new Set(data.map((_, idx) => idx)))}
              />
              {data.length > 0 && (
                <TableBody data={data} columns={columns} selectedRows={selectedRows} onToggleSelectRow={handleToggleSelectRow} onEdit={onEdit} onDelete={onDelete} onView={onView} noDeleteSelected={noDeleteSelected} itemForBg={itemForBg} />
              )}
            </table>
            {!data.length && <div className="bg-sidebar text-text w-full flex justify-center items-center p-4">{t("noAvailableData")}</div>}
          </div>
          {data.length > 0 && Math.ceil(total / rowsPerPage) > 1 && <TablePagination currentPage={currentPage} totalPages={Math.ceil(total / rowsPerPage)} onPageChange={handlePageChange} />}
        </>
      )}

      {isDuplicateModalOpen && (
        <Modal isOpen={isDuplicateModalOpen} title={t("duplication")} size="super" onClose={() => setIsDuplicateModalOpen(false)}>
          <Duplication submitData={onSubmit} isLoading={duplicateLoading} />
        </Modal>
      )}
    </div>
  );
};

export default Table;
